// src/features/authn/TokenToolsPanel.tsx
// Admin token diagnostics — IntrospectToken (inspect any access/refresh token
// issued on this platform) and RevokeToken (kill a leaked/compromised token),
// paired security-incident-response tools. Revocation requires typed confirmation.

import { useState } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { Button, Textarea, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, Badge, ErrorAlert } from '@react/components';
import type { IntrospectTokenResponse } from '@core/types/authn';

const REVOKE_KEYWORD = 'REVOKE';

export const TokenToolsPanel = observer(() => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [token, setToken] = useState('');
  const [result, setResult] = useState<IntrospectTokenResponse | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revoked, setRevoked] = useState(false);

  const handleInspect = async () => {
    if (!token.trim()) return;
    setInspecting(true);
    setError(null);
    setResult(null);
    setRevoked(false);
    try {
      const resp = await authnStore.IntrospectToken({
        entity: {} as never,
        token: token.trim(),
        token_type_hint: '',
        client_id: '',
        client_secret: '',
      });
      setResult(resp);
    } catch (err) {
      setError(err);
    } finally {
      setInspecting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    setError(null);
    try {
      await authnStore.RevokeToken({
        entity: {} as never,
        token: token.trim(),
        token_type_hint: '',
        client_id: '',
        client_secret: '',
      });
      setRevoked(true);
      setResult(null);
      setConfirmOpen(false);
      setConfirmText('');
    } catch (err) {
      setError(err);
    } finally {
      setRevoking(false);
    }
  };

  const claimRows: Array<{ label: string; value: string }> = result
    ? [
        { label: t('authn.admin.tokenType'), value: result.token_type || '-' },
        { label: t('authn.admin.scope'), value: result.scope || '-' },
        { label: t('authn.clientId'), value: result.client_id || '-' },
        { label: t('authn.admin.subject'), value: result.sub || '-' },
        { label: t('authn.admin.audience'), value: result.aud || '-' },
        { label: t('authn.admin.issuer'), value: result.iss || '-' },
        { label: t('authn.admin.jwtId'), value: result.jti || '-' },
        { label: t('authn.admin.expiresAt'), value: result.exp ? new Date(result.exp * 1000).toLocaleString() : '-' },
        { label: t('authn.admin.issuedAt'), value: result.iat ? new Date(result.iat * 1000).toLocaleString() : '-' },
      ]
    : [];

  return (
    <div className="space-y-4">
      {!!error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {revoked && <Alert variant="success" dismissible onDismiss={() => setRevoked(false)}>{t('authn.admin.tokenRevoked')}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('authn.admin.tokenInspectorTitle')}</CardTitle>
          <CardDescription>{t('authn.admin.tokenInspectorDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            label={t('authn.admin.tokenValue')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            rows={3}
            placeholder="eyJhbGciOi..."
          />
        </CardContent>
        <CardFooter className="justify-end">
          <Button onClick={handleInspect} isLoading={inspecting} disabled={!token.trim()}>
            {t('authn.admin.inspect')}
          </Button>
        </CardFooter>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('authn.admin.tokenClaims')}</CardTitle>
              <Badge variant={result.active ? 'success' : 'error'}>
                {result.active ? t('authn.admin.tokenActive') : t('authn.admin.tokenInactive')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              {claimRows.map((row) => (
                <div key={row.label} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{row.label}</dt>
                  <dd className="mt-0.5 text-sm text-foreground font-mono break-all sm:col-span-2 sm:mt-0">{row.value}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
          <CardFooter className="justify-end">
            <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
              {t('authn.admin.revokeThisToken')}
            </Button>
          </CardFooter>
        </Card>
      )}

      {confirmOpen && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">{t('authn.admin.revokeConfirmTitle')}</CardTitle>
            <CardDescription>{t('authn.admin.revokeConfirmDesc', { keyword: REVOKE_KEYWORD })}</CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={REVOKE_KEYWORD}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
              autoComplete="off"
            />
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setConfirmOpen(false); setConfirmText(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRevoke}
              isLoading={revoking}
              disabled={confirmText !== REVOKE_KEYWORD}
            >
              {t('authn.admin.revokeThisToken')}
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
});

export default TokenToolsPanel;
